<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      @click.self="$emit('close')"
    >
      <div
        data-testid="versions-overlay"
        class="w-full max-w-[945px] bg-secondary border border-tertiary p-8 rounded-[20px] shadow-2xl relative"
      >
        <h2
          class="text-2xl font-bold text-text-main mb-6 bg-primary border border-tertiary p-6 rounded-[20px]"
        >
          Versionen{{ projectName ? `: ${projectName}` : '' }}
        </h2>

        <div
          class="bg-primary border border-tertiary p-4 rounded-[20px] mb-8 min-h-[300px] max-h-[480px] overflow-y-auto"
        >
          <p v-if="versions.length === 0" class="p-4 text-center opacity-60">
            Keine Versionen vorhanden.
          </p>
          <ul v-else class="space-y-2">
            <li
              v-for="(version, index) in versions"
              :key="version.id"
              class="rounded-xl border transition-colors"
              :class="
                selectedId === version.id ? 'bg-accent/10 border-accent' : 'border-transparent'
              "
            >
              <!-- Header row: click to select + expand -->
              <button
                type="button"
                data-testid="version-entry"
                :data-message="version.message ?? ''"
                class="w-full flex items-center justify-between p-4 text-left cursor-pointer rounded-xl hover:bg-secondary transition-colors"
                :class="selectedId === version.id ? 'text-accent' : 'text-text-main'"
                @click="toggle(version.id)"
              >
                <span class="flex items-center gap-2 font-bold min-w-0">
                  <MdiIcon
                    :path="expandedId === version.id ? mdiChevronDown : mdiChevronRight"
                    size="20"
                    class="flex-shrink-0 opacity-70"
                  />
                  <span class="truncate">
                    Version {{ versions.length - index }}
                    <span class="ml-2 text-xs font-normal opacity-70">{{ version.kind }}</span>
                    <span v-if="version.message" class="ml-2 font-normal opacity-80">
                      — {{ version.message }}
                    </span>
                  </span>
                </span>
                <span class="opacity-60 text-sm flex-shrink-0 ml-3">
                  {{ formatDate(version.created_at) }}
                </span>
              </button>

              <!-- Expandable diff panel -->
              <div v-if="expandedId === version.id" class="px-4 pb-4">
                <div class="rounded-xl border border-tertiary bg-secondary p-3">
                  <p v-if="diffState(version.id).loading" class="text-sm opacity-60 p-2">
                    Änderungen werden geladen …
                  </p>
                  <p v-else-if="diffState(version.id).error" class="text-sm text-red-500 p-2">
                    Änderungen konnten nicht geladen werden.
                  </p>
                  <p
                    v-else-if="diffState(version.id).files.length === 0"
                    class="text-sm opacity-60 p-2"
                  >
                    Keine Dateiänderungen in dieser Version.
                  </p>
                  <div v-else class="space-y-3">
                    <div
                      v-for="file in diffState(version.id).files"
                      :key="file.path"
                      class="rounded-lg border border-tertiary overflow-hidden"
                    >
                      <div class="flex items-center gap-2 px-3 py-2 bg-primary text-sm font-mono">
                        <span
                          class="px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0"
                          :class="statusClass(file.status)"
                        >
                          {{ statusLabel(file.status) }}
                        </span>
                        <span class="truncate text-text-main">
                          <template v-if="file.status === 'renamed' && file.old_path">
                            {{ file.old_path }} → {{ file.path }}
                          </template>
                          <template v-else>{{ file.path }}</template>
                        </span>
                      </div>
                      <pre
                        v-if="diffLines(file.diff).length"
                        class="text-xs font-mono overflow-x-auto p-3 leading-relaxed bg-secondary"
                      ><span
                          v-for="(line, i) in diffLines(file.diff)"
                          :key="i"
                          class="block whitespace-pre"
                          :class="line.cls"
                      >{{ line.text || ' ' }}</span></pre>
                      <p v-else class="text-xs opacity-60 px-3 py-2">
                        Kein Inhaltsunterschied (nur Pfad geändert).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="versions-switch"
            class="px-8 py-2 bg-accent text-white text-sm font-semibold rounded-standard cursor-pointer transition-all duration-150 hover:brightness-110 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!selectedId"
            @click="onSwitch"
          >
            wechseln
          </button>
          <button
            type="button"
            class="px-6 py-2 bg-primary border border-tertiary text-text-main text-sm font-semibold rounded-standard cursor-pointer transition-colors duration-150 hover:bg-primary/80"
            @click="$emit('close')"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { mdiChevronRight, mdiChevronDown } from '@mdi/js'
import MdiIcon from '@/components/MdiIcon.vue'
import { ProjectService } from '@/services/projectService'
import type { VersionData, VersionFileDiff, VersionFileStatus } from '@/services/projectService'

const props = defineProps<{
  isOpen: boolean
  versions: VersionData[]
  projectName?: string
  projectId?: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'switch', versionId: string): void
}>()

const selectedId = ref<string | null>(null)
const expandedId = ref<string | null>(null)

type DiffState = { loading: boolean; error: boolean; files: VersionFileDiff[] }
// Per-version cache so re-expanding a version doesn't refetch its diff.
const diffCache = ref<Record<string, DiffState>>({})

// Reset selection/expansion and clear the cache each time the dialog opens.
watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      selectedId.value = null
      expandedId.value = null
      diffCache.value = {}
    }
  }
)

function diffState(versionId: string): DiffState {
  return diffCache.value[versionId] ?? { loading: false, error: false, files: [] }
}

async function toggle(versionId: string) {
  // Selecting a version also picks it for "wechseln".
  selectedId.value = versionId
  if (expandedId.value === versionId) {
    expandedId.value = null
    return
  }
  expandedId.value = versionId
  if (diffCache.value[versionId] || !props.projectId) return

  diffCache.value = {
    ...diffCache.value,
    [versionId]: { loading: true, error: false, files: [] },
  }
  try {
    const res = await ProjectService.getVersionDiff(props.projectId, versionId)
    diffCache.value = {
      ...diffCache.value,
      [versionId]: { loading: false, error: false, files: res.files ?? [] },
    }
  } catch (e) {
    console.error('Failed to fetch version diff', e)
    diffCache.value = {
      ...diffCache.value,
      [versionId]: { loading: false, error: true, files: [] },
    }
  }
}

// Git patch header noise the user does not need to see; hunk headers (@@) and the
// actual +/- content lines are kept.
const HEADER_PREFIXES = [
  'diff --git',
  'index ',
  '--- ',
  '+++ ',
  'new file mode',
  'deleted file mode',
  'old mode',
  'new mode',
  'similarity index',
  'rename from',
  'rename to',
]

function diffLines(diff: string): { text: string; cls: string }[] {
  if (!diff) return []
  const out: { text: string; cls: string }[] = []
  for (const line of diff.split('\n')) {
    if (HEADER_PREFIXES.some((p) => line.startsWith(p))) continue
    let cls = 'text-text-main/80'
    if (line.startsWith('@@')) cls = 'text-accent bg-accent/5'
    else if (line.startsWith('+')) cls = 'text-green-500 bg-green-500/5'
    else if (line.startsWith('-')) cls = 'text-red-500 bg-red-500/5'
    out.push({ text: line, cls })
  }
  // Drop trailing blank line from the split.
  while (out.length && out[out.length - 1].text === '') out.pop()
  return out
}

const STATUS_LABELS: Record<VersionFileStatus, string> = {
  added: 'Hinzugefügt',
  modified: 'Geändert',
  deleted: 'Gelöscht',
  renamed: 'Umbenannt',
}

function statusLabel(status: VersionFileStatus): string {
  return STATUS_LABELS[status] ?? status
}

function statusClass(status: VersionFileStatus): string {
  switch (status) {
    case 'added':
      return 'bg-green-500/15 text-green-500'
    case 'deleted':
      return 'bg-red-500/15 text-red-500'
    case 'renamed':
      return 'bg-blue-500/15 text-blue-500'
    default:
      return 'bg-accent/15 text-accent'
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function onSwitch() {
  if (selectedId.value) emit('switch', selectedId.value)
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
